import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tag, Spin } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useC5M5, ItemState } from './hooks/useC5M5';
import { c5m5Api } from '../../services/c5m5Api';
import { BatchAcceptanceBridge, pwc5m5f1AcceptanceManifest } from '../../features/acceptance';
import './C5M5Form.css';

// ── Helpers ──────────────────────────────────────────────────
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

// ── Component ────────────────────────────────────────────────
const C5M5Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleExecute,
    lastApiResponse,
  } = useC5M5();

  // ── Query ──
  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ── Execute ──
  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆銷貨退回單？此操作將回沖庫存與金額。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  // ── Table columns ──
  const columns: ColumnsType<ItemState> = [
    {
      title: '沖銷',
      dataIndex: 'selected',
      key: 'selected',
      width: 60,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        if (record.validating) {
          return <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} />} />;
        }
        return (
          <Checkbox
            checked={record.selected}
            data-acceptance-action="PWC5M5-ACT-VALIDATE-ITEM"
            onChange={(e) =>
              handleToggleItem(record.item.fc130_01, e.target.checked)
            }
          />
        );
      },
    },
    {
      title: '銷貨退回單號',
      dataIndex: ['item', 'fc130_01'],
      key: 'fc130_01',
      width: 160,
      render: (text: string, record: ItemState) => (
        <span>
          {text}
          {record.valid === true && (
            <CheckCircleOutlined
              className="status-valid"
              style={{ marginLeft: 6 }}
            />
          )}
          {record.valid === false && (
            <CloseCircleOutlined
              className="status-invalid"
              style={{ marginLeft: 6 }}
              title={record.validationMsg}
            />
          )}
        </span>
      ),
    },
    {
      title: '退回日期',
      dataIndex: ['item', 'fc130_11'],
      key: 'fc130_11',
      width: 120,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '入庫庫位',
      key: 'fc130_12',
      width: 160,
      render: (_: unknown, record: ItemState) => {
        const id = record.item.fc130_12_id;
        const name = record.item.fc130_12;
        if (id && name) return `${id} - ${name}`;
        return id || name || '';
      },
    },
    {
      title: '總退回數量',
      dataIndex: ['item', 'fc130_13'],
      key: 'fc130_13',
      width: 120,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        if (record.valid === true) return <Tag color="success">可沖銷</Tag>;
        if (record.valid === false) return <Tag color="error">不可沖</Tag>;
        return <Tag>待驗證</Tag>;
      },
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="c5m5-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                銷貨管理
              </a>
              <span>/</span>
              <span className="current">銷貨退回沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>銷貨退回單核准沖</h1>
            <span className="badge">C5M5</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>銷貨退回單 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWC5M5-ACT-QUERY">
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fc130_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入銷貨退回單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWC5M5-ACT-EXECUTE"
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
            data-acceptance-action="PWC5M5-ACT-EXIT"
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwc5m5f1AcceptanceManifest}
          records={items}
          selectedKeys={items.filter((item) => item.selected).map((item) => item.item.fc130_01)}
          keyOf={(item) => item.item.fc130_01}
          queryExact={(key) => c5m5Api.query(key, key)}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items[0];
            if (first) void handleToggleItem(first.item.fc130_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="自動填表只驗證並勾選第一筆候選單，不會自動執行沖銷。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter((item) => item.selected) }}
        />
      </div>
    </div>
  );
};

export default C5M5Form;
